// The onGuard score history (ENTRAID-347 redesign, implementation plan B1): one point per local calendar day, the
// last run of that day with a score. Manual runs and retries on the same day collapse into one point. A missing
// Microsoft Secure Score is a gap (null), never a zero. `coversDays` says truthfully how many days the history
// reaches back (the runs list holds at most 100 rows), so a caption never claims 30 days it does not have.

import { formatScore } from './score-format.js';

const finite = (n) => typeof n === 'number' && Number.isFinite(n);
const pad = (n) => String(n).padStart(2, '0');

/** The local calendar day 'YYYY-MM-DD' of a timestamp (ISO string, epoch ms or Date). Null when unparseable. */
export function localDayKey(ts) {
  const d = ts instanceof Date ? ts : new Date(typeof ts === 'number' ? ts : Date.parse(ts ?? ''));
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Whole days from day key a to day key b (calendar days, so a daylight saving change never counts). */
export function daysBetween(a, b) {
  const n = (k) => {
    const [y, m, d] = k.split('-').map(Number);
    return Date.UTC(y, m - 1, d) / 86400000;
  };
  return Math.round(n(b) - n(a));
}

/**
 * @param {Array<object>} runs  rows of GET /customers/{id}/runs, any order
 * @param {{ days?: number, now?: Date|number }} [opts]
 * @returns {{ points: Array<{ day: string, score: number, secureScorePct: number|null, runTimestamp: string }>,
 *   firstDay: string|null, lastDay: string|null, today: string, coversDays: number }}
 */
export function scoreHistory(runs, { days = 30, now = Date.now() } = {}) {
  const today = localDayKey(now);
  const byDay = new Map();
  for (const r of Array.isArray(runs) ? runs : []) {
    const score = r?.securityPosture?.score;
    if (!finite(score)) continue;
    const t = Date.parse(r.runTimestamp ?? '');
    if (!Number.isFinite(t)) continue;
    const day = localDayKey(t);
    const age = daysBetween(day, today);
    if (age < 0 || age >= days) continue;
    const held = byDay.get(day);
    if (!held || t > held.t) byDay.set(day, { t, run: r });
  }
  const points = [...byDay.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([day, { run }]) => {
      const pct = run.securityPosture?.msSecureScore?.percentage;
      return { day, score: run.securityPosture.score, secureScorePct: finite(pct) ? pct : null, runTimestamp: run.runTimestamp };
    });
  const firstDay = points[0]?.day ?? null;
  const lastDay = points.at(-1)?.day ?? null;
  const coversDays = firstDay ? Math.min(days, daysBetween(firstDay, today) + 1) : 0;
  return { points, firstDay, lastDay, today, coversDays };
}

/**
 * The trend's one-sentence summary for the chart's accessible name and the KPI caption, or null with fewer than
 * 2 points. "onGuard score rose from 77% to 86% over the last 30 days".
 */
export function trendSummary(history, label = 'onGuard score') {
  const { points, coversDays } = history ?? {};
  if (!Array.isArray(points) || points.length < 2) return null;
  const a = points[0].score;
  const b = points.at(-1).score;
  const span = `over the last ${coversDays} ${coversDays === 1 ? 'day' : 'days'}`;
  if (a === b) return `${label} held at ${formatScore(b)} ${span}`;
  return `${label} ${b > a ? 'rose' : 'fell'} from ${formatScore(a)} to ${formatScore(b)} ${span}`;
}

/**
 * The KPI caption from the trend ("Up 9 points in 30 days"), only when the history covers at least `minDays` days
 * (default 7) with 2 real scores. Null otherwise, so the caller falls back to its neutral caption.
 */
export function trendCaption(history, { minDays = 7 } = {}) {
  const { points, coversDays } = history ?? {};
  if (!Array.isArray(points) || points.length < 2 || coversDays < minDays) return null;
  const d = points.at(-1).score - points[0].score;
  const pts = (n) => `${n} ${n === 1 ? 'point' : 'points'}`;
  if (d === 0) return `No change in ${coversDays} days`;
  return `${d > 0 ? 'Up' : 'Down'} ${pts(Math.abs(d))} in ${coversDays} days`;
}
